import { makeAutoObservable } from 'mobx'
import { blogsApi, uploadsApi } from '@/api'
import { addCatchNotification } from '@/modules/notifications'
import {
  BlogCategorySingleResponseType,
  IBlogCategoriesEdit,
  IBlogCategoriesParams,
  IBlogCategoriesRequest,
  IBlogCategoryParams,
  IBlogCategorySingle,
  IBlogEditParams,
  IBlogModalParams,
  IBlogResponse,
  IBlogsCategoriesResponse,
  IBlogsParams,
  IBlogsResponse,
  ITopBlogResponse,
  Statuses,
  TBlogSingleValues,
} from '@/types'
import { IAddBlogCategory } from '@/types/banner'

class BlogsStore {
  blogCategories: IBlogsCategoriesResponse | null = null
  blogCategoriesModal = false
  topBlogs: ITopBlogResponse | null = null
  recommendedBlogs: ITopBlogResponse | null = null
  blogs: IBlogsResponse | null = null
  modalState: IBlogModalParams | null = null
  blogSingle: IBlogResponse | null = null
  blogCategory: IBlogCategorySingle | null = null
  blogPosterUrl: string | null = null
  blogPosterModal = false
  isAvailable = false
  blogCategorySingle: BlogCategorySingleResponseType | null = null
  activeBlogStatus = Statuses.Active
  activeCategory = Statuses.Active
  blogsLoading = false
  byCategoriesLoading = false
  recommendBlogsLoading = false
  topBlogsLoading = false
  blogsFilter: IBlogsParams = {
    page: 1,
    perPage: 10,
  }

  constructor() {
    makeAutoObservable(this)
  }

  setByCategoriesLoading = (params: boolean) => {
    this.byCategoriesLoading = params
  }

  setActiveCategory = (params: Statuses) => {
    this.activeCategory = params
  }

  setBlogsFilter = (params: IBlogsParams) => {
    this.blogsFilter = params
  }

  setRecommendBlogsLoading = (params: boolean) => {
    this.recommendBlogsLoading = params
  }

  setTopBlogsLoading = (params: boolean) => {
    this.topBlogsLoading = params
  }

  setBlogsLoading = (params: boolean) => {
    this.blogsLoading = params
  }

  setActiveBlogStatus = (params: Statuses) => {
    this.activeBlogStatus = params
  }

  setIsAvailable = (isAvailable: boolean) => {
    this.isAvailable = isAvailable
  }

  setBlogCategories = (data: IBlogsCategoriesResponse | null) => {
    this.blogCategories = data
  }

  setTopBlogs = (data: ITopBlogResponse | null) => {
    this.topBlogs = data
  }

  setRecommendedBlog = (data: ITopBlogResponse | null) => {
    this.recommendedBlogs = data
  }

  setBlogSingle = (data: IBlogResponse | null) => {
    this.blogSingle = data
  }

  setBlogCategory = (data: IBlogCategorySingle | null) => {
    this.blogCategory = data
  }

  getBlogCategories = (params?: IBlogCategoriesRequest) =>
    blogsApi
      .getBlogCategories(params)
      .then((res) => {
        if (res.success) {
          this.setBlogCategories(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setTopBlogsLoading(false))

  editBlogCategories = (params: IBlogCategoriesEdit) => blogsApi.editBlogCategory(params)

  getTopBlogs = () =>
    blogsApi
      .getTopBlogs()
      .then((res) => {
        if (res.success) {
          this.setTopBlogs(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setTopBlogsLoading(false))

  getRecommendedBlogs = () =>
    blogsApi
      .getRecommendedBlogs()
      .then((res) => {
        if (res.success) {
          this.setRecommendedBlog(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setRecommendBlogsLoading(false))

  uploadBlogBanner = (params: any) => uploadsApi.uploadBlogBanner(params)

  setBlogs = (blogs: IBlogsResponse | null) => {
    this.blogs = blogs
  }

  getBlogs = () =>
    blogsApi
      .getBlogs({
        ...this.blogsFilter,
        status: this.activeBlogStatus,
      })
      .then((res) => {
        if (res.success) {
          this.setBlogs(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setBlogsLoading(false))

  addBlog = (params: TBlogSingleValues) => blogsApi.addBlog(params)

  getSingleBlog = (params: string) =>
    blogsApi
      .getSingleBlog(params)
      .then((res) => {
        if (res.success) {
          this.setBlogSingle(res.data)
        }

        return res
      })
      .catch(addCatchNotification)

  getBlogsByCategory = (params: IBlogCategoryParams) =>
    blogsApi
      .getBlogCategory({
        ...params,
        status: this.activeCategory,
      })
      .then((res) => {
        if (res.success) {
          this.setBlogCategory(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setByCategoriesLoading(false))

  setSingleBlogCategory = (data: BlogCategorySingleResponseType) => {
    this.blogCategorySingle = data
  }

  getSingleBlogCategory = (param: number) =>
    blogsApi
      .getBlogCategorySingle(param)
      .then((res) => {
        if (res.success) {
          this.setSingleBlogCategory(res.data)
        }
      })
      .catch(addCatchNotification)

  editBlog = (params: IBlogEditParams) => blogsApi.editBlog(params)

  archiveBlog = (id: number) => blogsApi.archiveBlog(id)

  unArchiveBlog = (id: number) => blogsApi.unArchiveBlog(id)

  toggleBlogModal = (params: IBlogModalParams) => {
    this.modalState = params
  }

  addBlogCategory = (params: IAddBlogCategory) => blogsApi.addBlogCategory(params)

  unTopBlog = (id: number) => blogsApi.unTopBlog(id)

  toTopBlog = (id: number) => blogsApi.toTopBlog(id)

  recommendBlog = (id: number) => blogsApi.recommendBlog(id)

  unRecommendBlog = (id: number) => blogsApi.unRecommendBlog(id)

  deleteBlogCategory = (params: number) => blogsApi.deleteBlogCategory(params)

  toggleBlogCategoriesModal = (status: boolean) => {
    this.blogCategoriesModal = status
  }

  setBlogPosterUrl = (url: string | null) => {
    this.blogPosterUrl = url
  }

  setBlogPosterModal = (value: boolean) => {
    this.blogPosterModal = value
  }

  reset = () => {
    this.blogsFilter = { page: 1, perPage: 10 }
    this.blogCategories = null
    this.blogCategoriesModal = false
    this.topBlogs = null
    this.recommendedBlogs = null
    this.blogs = null
    this.modalState = null
    this.blogSingle = null
    this.isAvailable = false
  }
}

export const blogsStore = new BlogsStore()
